import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { Award, Flame, Download, ArrowLeft, ShieldCheck, Copy, CheckCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

export default function Certificate() {
  const { user } = useAuth();
  const [progress, setProgress] = useState(null);
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);
  const certRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [p, c] = await Promise.allSettled([
          api.get("/progress"),
          api.get("/certificate/me"),
        ]);
        if (p.status === "fulfilled") setProgress(p.value.data);
        if (c.status === "fulfilled") setCert(c.value.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const verifyUrl = cert
    ? `${window.location.origin}/verify/${cert.id}`
    : "";

  const downloadPdf = async () => {
    if (!certRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(certRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "pt",
        format: "letter",
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const ratio = canvas.width / canvas.height;
      let imgW = pageWidth - 40;
      let imgH = imgW / ratio;
      if (imgH > pageHeight - 40) {
        imgH = pageHeight - 40;
        imgW = imgH * ratio;
      }
      const x = (pageWidth - imgW) / 2;
      const y = (pageHeight - imgH) / 2;
      pdf.addImage(imgData, "PNG", x, y, imgW, imgH);
      const safe = (user?.full_name || "student").replace(/\s+/g, "_");
      pdf.save(`APM_Certificate_${safe}_${cert?.id || ""}.pdf`);
      toast.success("Certificate PDF downloaded");
    } catch {
      toast.error("Could not generate PDF — try Print as fallback");
    } finally {
      setDownloading(false);
    }
  };

  const copyVerify = async () => {
    if (!verifyUrl) return;
    try {
      await navigator.clipboard.writeText(verifyUrl);
      setCopied(true);
      toast.success("Verification URL copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-20 text-zinc-500">Loading…</div>
      </div>
    );
  }

  const earned = progress?.certificate_earned;
  const exam = progress?.final_exam;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 py-12" data-testid="certificate-page">
        <Link to="/dashboard" className="text-sm text-zinc-600 hover:text-red-600 inline-flex items-center gap-1 mb-6 print:hidden">
          <ArrowLeft className="w-4 h-4" /> Dashboard
        </Link>

        {!earned || !cert ? (
          <div className="card-tech p-10 text-center">
            <Award className="w-10 h-10 text-zinc-400 mx-auto mb-4" />
            <h1 className="font-heading text-3xl font-bold tracking-tight mb-2">Not quite yet</h1>
            <p className="text-zinc-600">Pass the final practice exam (70%+) to unlock your certificate.</p>
            <Link to="/final-exam" className="btn-primary mt-6 inline-flex">Take the exam</Link>
          </div>
        ) : (
          <>
            <div
              ref={certRef}
              className="border-4 border-red-600 p-10 sm:p-16 bg-white relative"
              data-testid="certificate-box"
            >
              <div className="grid-bg absolute inset-0 opacity-30 pointer-events-none" />
              <div className="relative">
                <div className="flex items-center justify-between mb-10">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-red-600 grid place-items-center">
                      <Flame className="w-5 h-5 text-white" strokeWidth={2.5} />
                    </div>
                    <div className="leading-tight">
                      <div className="font-heading font-bold text-lg">APM Fire Alarm Exam Prep</div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-red-600">Certificate of completion</div>
                    </div>
                  </div>
                  <Award className="w-14 h-14 text-red-600" strokeWidth={1.25} />
                </div>

                <div className="overline mb-2"><span className="red-bar" />Awarded to</div>
                <div className="font-heading text-5xl sm:text-6xl font-bold tracking-tighter mb-6" data-testid="certificate-name">
                  {user?.full_name}
                </div>

                <p className="text-zinc-700 max-w-2xl leading-relaxed">
                  For successfully completing the {progress?.total_hours || 70}-hour{" "}
                  <span className="font-semibold">NICET Level 1 Foundation</span> curriculum,
                  covering fire detection principles, NFPA 72 fundamentals, installation standards, power supplies,
                  ITM procedures, and passing the 50-question practice exam with a score of{" "}
                  <span className="font-bold">{exam?.percentage}%</span>.
                </p>

                <div className="mt-10 grid sm:grid-cols-3 gap-4 border-t border-zinc-200 pt-6">
                  <div>
                    <div className="overline">Hours completed</div>
                    <div className="font-heading text-2xl font-bold">{progress?.hours_completed} / {progress?.total_hours}</div>
                  </div>
                  <div>
                    <div className="overline">Exam score</div>
                    <div className="font-heading text-2xl font-bold">{exam?.percentage}%</div>
                  </div>
                  <div>
                    <div className="overline">Issued</div>
                    <div className="font-heading text-2xl font-bold">{new Date(cert.issued_at).toLocaleDateString()}</div>
                  </div>
                </div>

                {/* QR + ID block */}
                <div className="mt-10 pt-6 border-t border-zinc-200 flex items-end justify-between gap-6 flex-wrap">
                  <div>
                    <div className="overline">Credential ID</div>
                    <div className="font-mono text-lg font-bold text-zinc-950 tracking-wide" data-testid="certificate-id">
                      {cert.id}
                    </div>
                    <div className="overline mt-3">Verify online</div>
                    <div className="font-mono text-[11px] text-zinc-600 break-all max-w-xs" data-testid="certificate-verify-url">
                      {verifyUrl}
                    </div>
                  </div>
                  <div className="bg-white p-2 border border-zinc-200" data-testid="certificate-qr">
                    <QRCodeSVG value={verifyUrl} size={120} bgColor="#ffffff" fgColor="#dc2626" level="M" />
                    <div className="font-mono text-[8px] text-center text-zinc-500 mt-1 tracking-wider uppercase">
                      Scan to verify
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex gap-3 flex-wrap print:hidden">
              <button
                onClick={downloadPdf}
                disabled={downloading}
                className="btn-primary disabled:opacity-50"
                data-testid="download-pdf-btn"
              >
                <Download className="w-4 h-4" />
                {downloading ? "Generating PDF…" : "Download PDF"}
              </button>
              <button onClick={() => window.print()} className="btn-outline" data-testid="print-certificate-btn">
                Print
              </button>
              <button onClick={copyVerify} className="btn-outline" data-testid="copy-verify-btn">
                {copied ? <CheckCheck className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? "Copied" : "Copy verify link"}
              </button>
              <a
                href={verifyUrl}
                target="_blank"
                rel="noreferrer"
                className="btn-outline"
                data-testid="open-verify-btn"
              >
                <ShieldCheck className="w-4 h-4" /> Open verification page
              </a>
              <Link to="/dashboard"><button className="btn-outline">Back to dashboard</button></Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
